import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDarkMode } from '../context/DarkModeContext';
import { supabase } from '../utils/supabase';
import { createLogger } from '../utils/logger';
import { useToast } from '../context/ToastContext';

const logger = createLogger({ module: 'MFAVerification', level: 'info' });

interface MFAVerificationProps {
  factorId: string;
  onSuccess: () => void;
  onError: (error: Error) => void;
  onCancel: () => void;
}

const MFAVerification: React.FC<MFAVerificationProps> = ({
  factorId,
  onSuccess,
  onError,
  onCancel
}) => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { darkMode } = useDarkMode();
  const { showToast } = useToast();
  const verifyTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const submitAttemptCount = useRef(0);

  useEffect(() => {
    return () => {
      // Clean up any pending timeouts
      if (verifyTimeoutRef.current) {
        clearTimeout(verifyTimeoutRef.current);
      }
    };
  }, []);

  // Add meta tag for mobile Safari 2FA detection
  useEffect(() => {
    // Add meta tag to help mobile Safari recognize this as a 2FA form
    const metaTag = document.createElement('meta');
    metaTag.name = 'format-detection';
    metaTag.content = 'telephone=no';
    document.head.appendChild(metaTag);

    // Also add viewport meta for better mobile experience if not present
    const existingViewport = document.querySelector('meta[name="viewport"]');
    if (!existingViewport) {
      const viewportMeta = document.createElement('meta');
      viewportMeta.name = 'viewport';
      viewportMeta.content = 'width=device-width, initial-scale=1.0, user-scalable=no';
      document.head.appendChild(viewportMeta);
    }

    return () => {
      // Clean up meta tags when component unmounts
      document.head.removeChild(metaTag);
      if (!existingViewport) {
        const addedViewport = document.querySelector('meta[name="viewport"]');
        if (addedViewport) {
          document.head.removeChild(addedViewport);
        }
      }
    };
  }, []);

  useEffect(() => {
    // Clear error when code changes
    if (error) setError(null);
    
    // Auto-submit if code is 6 digits
    if (code.length === 6 && !loading) {
      // Check if the code is valid (all digits)
      if (/^\d{6}$/.test(code)) {
        // Use a small delay to let the user see they've entered all 6 digits
        if (verifyTimeoutRef.current) {
          clearTimeout(verifyTimeoutRef.current);
        }
        
        verifyTimeoutRef.current = setTimeout(() => {
          verifyTimeoutRef.current = null;
          handleSubmit();
        }, 300);
      }
    }
  }, [code]);

  const verifyCode = async () => {
    try {
      logger.debug('Starting MFA verification with challengeAndVerify', {
        factorId: factorId.substring(0, 8) + '...',
        codeLength: code.length
      });

      // Use challengeAndVerify to avoid IP mismatch issues
      const { data, error: verifyError } = await supabase.auth.mfa.challengeAndVerify({
        factorId,
        challengeType: 'totp',
        code
      } as any); // Use 'as any' to bypass typescript error until Supabase types are fixed

      if (verifyError) {
        logger.error('MFA verification failed:', verifyError);
        throw verifyError;
      }

      if (data) {
        logger.debug('MFA verification successful');
        return true;
      }

      // If verification was successful but no data was returned
      logger.warn('MFA verification successful but no data was returned');
      return true;
    } catch (parseError) {
      // Check if this might be a JSON parsing error from HTML response
      if (parseError instanceof SyntaxError && parseError.message.includes('Unexpected token')) {
        logger.error('Received HTML instead of JSON during MFA verification:', parseError);
        setError('Authentication service returned an invalid response. Please try again later.');
        onError(new Error('Invalid response format from authentication service'));
        return false;
      }
      
      // For other errors, log and return
      logger.error('Exception during MFA verification:', parseError);
      setError(parseError instanceof Error ? parseError.message : 'Failed to verify code');
      onError(parseError instanceof Error ? parseError : new Error('Failed to verify code'));
      return false;
    }
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    
    if (!code.trim()) {
      setError('Please enter a verification code');
      return;
    }

    // Validate factor ID
    if (!factorId || factorId === "factor_id must be an UUID") {
      setError('Invalid MFA configuration. Please try logging in again.');
      onError(new Error('Invalid factor ID'));
      return;
    }

    // Check for rapid multiple submissions
    submitAttemptCount.current += 1;
    if (submitAttemptCount.current > 3) {
      // Add a small delay to prevent throttling
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
    
    // Don't allow concurrent verification attempts
    if (loading) return;
    
    setLoading(true);
    setError(null);

    try {
      const success = await verifyCode();
      if (success) {
        onSuccess();
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`p-8 rounded-lg shadow-lg ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
      <div className="text-center mb-8">
        <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          Two-Factor Authentication
        </h2>
        <p className={`mt-2 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          Please enter the verification code from your authenticator app
        </p>
      </div>
      <form onSubmit={handleSubmit} autoComplete="on" data-form-type="mfa-verification">
        <div className="space-y-4">
          <div>
            <label htmlFor="code" className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Verification Code
            </label>
            <p id="code-help" className={`mt-1 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Enter the 6-digit code from your authenticator app
            </p>
            <input
              id="code"
              name="code"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              autoComplete="one-time-code"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              data-testid="mfa-code-input"
              autoFocus
              required
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              className={`mt-2 block w-full px-3 py-2 border ${
                darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
              } rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-center text-lg tracking-widest font-mono`}
              placeholder="000000"
              aria-label="Enter 6-digit verification code from your authenticator app"
              aria-describedby="code-help"
            />
          </div>

          {error && (
            <div className="rounded-md bg-red-50 dark:bg-red-900/50 p-4">
              <div className="flex">
                <div className="shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className={`text-sm ${darkMode ? 'text-red-200' : 'text-red-800'}`}>
                    {error}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="flex space-x-4">
            <button
              type="submit"
              disabled={loading}
              className={`flex-1 flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                loading
                  ? 'bg-blue-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
              }`}
            >
              {loading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Verifying...
                </>
              ) : (
                'Verify'
              )}
            </button>
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              className={`flex-1 py-2 px-4 border rounded-md shadow-sm text-sm font-medium ${
                darkMode
                  ? 'border-gray-700 text-gray-300 hover:bg-gray-700'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              Cancel
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default MFAVerification; 